//import { format, parseISO } from 'date-fns';
import React from 'react';
import { openCalendarInvite } from '../utils/calendarInvite.js';
import { CalendarIcon, PlusIcon } from '@heroicons/react/24/outline';
import { saveEventToUser } from '../utils/eventActions.js';
import { userSession } from '../utils/userSession.js';
import { useState, useEffect } from 'react';
import StatusOverlay from './StatusOverlay.js';
import { trackEvent, EventTypes } from '../utils/analytics.js';
import PartnerCarousel from './PartnerCarousel.js';
import { useData } from '../context/DataContext.js';

export default function EventCardView({ events, onOpenLogin, onEventAdded }) {
  // Get partners from context - useData must be called unconditionally
  const { partners } = useData();
  
  // Filter active partners safely
  const activePartners = partners?.filter(partner => partner?.isPaid === 1) || [];
  const [statusMessage, setStatusMessage] = useState('');
  const [showStatus, setShowStatus] = useState(false);
  const [statusType, setStatusType] = useState('success');
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport (less than 768px = md breakpoint)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const showStatusMessage = (message, type = 'success') => {
    setStatusMessage(message);
    setStatusType(type);
    setShowStatus(true);
    setTimeout(() => {
      setShowStatus(false);
      setStatusMessage('');
      setStatusType('success');
    }, 2000);
  };

  const handleAddEvent = async (eventId) => {
    if (!userSession.isLoggedIn()) {
      onOpenLogin("Sign in to add events to DriftCal");
      return;
    }

    try {
      const result = await saveEventToUser(null, eventId);
      showStatusMessage(
        result.alreadySaved ? 'Event already saved' : 'Event added',
        result.alreadySaved ? 'warning' : 'success'
      );
      // Notify parent that an event was added
      if (!result.alreadySaved) {
        onEventAdded();
      }
    } catch (error) {
      console.error('Error in handleAddEvent:', {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
        eventId,
        userSession: {
          isLoggedIn: userSession.isLoggedIn(),
          hasUser: !!userSession.getUser(),
          userId: userSession.getUserId()
        }
      });
      showStatusMessage('Failed to add event', 'error');
    }
  };

  const formatDateTime = (dateString, currentEvent) => {
    try {
      const [datePart, timePart] = dateString.split('T');
      const [fullYear, month, day] = datePart.split('-');
      const [hours, minutes] = timePart.split(':');

      // Convert to 12-hour format
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'pm' : 'am';
      const hour12 = hour % 12 || 12;

      // Convert month number to abbreviated name
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[parseInt(month) - 1];

      // Check if we only need to show time
      if (dateString === currentEvent.EndDateTime && 
          isSameDay(currentEvent.StartDateTime, currentEvent.EndDateTime)) {
        return `${hour12}:${minutes.padStart(2, '0')} ${ampm}`;
      }

      // For start date or different days
      return `${day}-${monthName}-${fullYear.slice(-2)}, ${hour12}:${minutes.padStart(2, '0')} ${ampm}`;
    } catch (error) {
      return dateString;
    }
  };

  const isSameDay = (date1, date2) => {
    return date1.split('T')[0] === date2.split('T')[0];
  };

  const getEventTypeStyles = (eventType) => {
    switch(eventType) {
      case 'Beginner':
        return 'bg-blue-300 text-blue-800';
      case 'Practice':
        return 'bg-green-200 text-green-800';
      case 'Competition':
        return 'bg-orange-200 text-orange-800';
      case 'Exhibition':
        return 'bg-red-200 text-red-800';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  const handleCalendarInvite = (event) => {
    // Track the calendar download event
    trackEvent(EventTypes.ICAL_DOWNLOAD, {
      event_id: event.EventID,
      event_name: event.EventName,
      promoter_id: event.OrganiserID,
      promoter_name: event.BusinessName,
      user_id: userSession.getUserId() || 'anonymous'
    });
    
    // Open the calendar invite
    openCalendarInvite(event);
  };

  const handleEventNameClick = (event) => {
    trackEvent(EventTypes.EVENT_CLICK, {
      event_id: event.EventID,
      event_name: event.EventName,
      promoter_id: event.OrganiserID,
      promoter_name: event.BusinessName,
      view_type: 'card',
      click_target: 'title',
      user_id: userSession.getUserId() || 'anonymous'
    });
  };

  const getStateStyles = (stateCode) => {
    switch(stateCode) {
      case 'VIC':
        return 'bg-gray-500';
      case 'NSW':
        return 'bg-sky-500';
      case 'ACT':
        return 'bg-blue-500';
      case 'QLD':
        return 'bg-rose-800';
      case 'SA':
        return 'bg-red-600';
      case 'NT':
        return 'bg-orange-700';
      case 'WA':
        return 'bg-yellow-500';
      case 'TAS':
        return 'bg-green-600';
      default:
        return 'bg-slate-900';
    }
  };

  const renderEventCard = (event, index) => {
    const [datePart] = event.StartDateTime.split('T');
    const [, month, day] = datePart.split('-');
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 
                       'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const monthDisplay = monthNames[parseInt(month) - 1];
    
    return (
      <div
        key={`${event.BusinessName}-${event.StartDateTime}-${index}`}
        className="rounded-xl shadow-lg overflow-hidden transform transition duration-200 hover:scale-105 hover:shadow-xl flex flex-col h-[300px] sm:h-[380px]"
      >
        {/* Date badge */}
        <div className="absolute top-2 left-2 w-12 sm:top-4 sm:left-4 sm:w-16 bg-slate-900/90 text-white rounded-lg overflow-hidden shadow-lg z-10">
          <div className="text-center py-1 text-2xl sm:text-4xl font-bold">
            {parseInt(day)}
          </div>
          <div className="text-center bg-slate-800/60 py-1 text-xs sm:text-m">
            {monthDisplay}
          </div>
        </div>

        {/* Image section */}
        <div className="h-24 sm:h-32 bg-gradient-to-br from-slate-600 to-slate-800 overflow-hidden relative shrink-0">
          {event.organiserImage && (
            <div className="absolute inset-0 transform hover:scale-110 transition-transform duration-500 flex items-center justify-center">
              <div className="w-[120%] h-[120%] -rotate-12 absolute -inset-[10%] flex items-center justify-center">
                <img
                  src={event.organiserImage}
                  alt=""
                  className="w-72 h-auto object-contain"
                />
              </div>
            </div>
          )}
        </div>

        {/* Main content wrapper - add flex-1 to allow it to grow/shrink */}
        <div className="flex-1 bg-white h-full">
          <div className="p-3 sm:p-5">
            {/* Tags */}
            <div className="flex gap-1 sm:gap-2 mb-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${getStateStyles(event.StateNameShort)} text-white`}>
                {event.StateNameShort}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${getEventTypeStyles(event.EventType)}`}>
                {event.EventType}
              </span>
            </div>

            {/* Event details */}
            <h3 className="text-[12px] sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2 line-clamp-2">
              {event.EventLink ? (
                <a 
                  href={event.EventLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleEventNameClick(event)}
                  className="hover:underline underline truncate block"
                >
                  {event.EventType === 'Competition' && event.RoundNum
                    ? `${event.EventName}, Round${event.RoundNum}`
                    : event.EventName}
                </a>
              ) : (
                <span className="truncate block">
                  {event.EventType === 'Competition' && event.RoundNum
                    ? `${event.EventName}, Round${event.RoundNum}`
                    : event.EventName}
                </span>
              )}
            </h3>

            <div className="text-xs sm:text-sm text-gray-900 mb-0 line-clamp-1">
              {event.VenueName && (
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.VenueName)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="underline hover:no-underline"
                >
                  {event.VenueName}
                </a>
              )}
            </div>

            <div className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2 line-clamp-1">
              {event.LayoutName}
            </div>

            <p className="text-xs sm:text-sm text-gray-600 mb-0 line-clamp-1">
              Start: {formatDateTime(event.StartDateTime, event)}
            </p>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-1">
              End: {formatDateTime(event.EndDateTime, event)}
            </p>
          </div>

          {/* Action buttons - now will stay at bottom */}
          <div className="mt-auto border-t border-gray-200 bg-white">
            <div className="grid grid-cols-2 w-full">
              {/* iCal button */}
              <div className="flex flex-col items-center justify-center p-2">
                <button
                  onClick={() => handleCalendarInvite(event)}
                  className="inline-flex items-center justify-center rounded-md p-0 sm:p-1 text-slate-900 hover:text-gray-800 h-8 w-8"
                  title="Add to Calendar"
                >
                  <CalendarIcon className="h-6 w-6 sm:h-6 sm:w-6" />
                </button>
                <span className="text-[8px] sm:text-[10px] text-slate-600 whitespace-nowrap">Download iCal</span>
              </div>

              {/* DriftCal button */}
              <div className="flex flex-col items-center justify-center p-2 border-l border-gray-200">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!userSession.isLoggedIn()) {
                      onOpenLogin("Sign in to add events to DriftCal");
                      return;
                    }
                    handleAddEvent(event.EventID);
                  }}
                  className="inline-flex items-center justify-center rounded-md p-0 sm:p-0 text-slate-900 hover:text-gray-800 h-8 w-8"
                  title="Add to my driftCal"
                >
                  <PlusIcon className="h-6 w-6 sm:h-6 sm:w-6" />
                </button>
                <span className="text-[8px] sm:text-[10px] text-slate-600 whitespace-nowrap">Add to DriftCal</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Status Overlay */}
      {showStatus && (
        <StatusOverlay 
          message={statusMessage} 
          type={statusType}
        />
      )}

      {/* Update grid classes for better mobile layout */}
      <div className="space-y-6">
        {/* Chunk events based on viewport: 4 events on mobile, 8 events on desktop */}
        {Array.from({ length: Math.ceil(events.length / (isMobile ? 4 : 8)) }, (_, chunkIndex) => {
          const chunkSize = isMobile ? 4 : 8;
          const chunkStart = chunkIndex * chunkSize;
          const chunkEvents = events.slice(chunkStart, chunkStart + chunkSize);
          
          // Show large carousel before each chunk (including the first one)
          const shouldShowCarousel = activePartners.length > 0;
          
          return (
            <div key={`chunk-${chunkIndex}`} className="space-y-6">
              {/* Show large PartnerCarousel before each chunk */}
              {shouldShowCarousel && (
                <div className="w-full">
                  <PartnerCarousel partners={activePartners} size="large" />
                </div>
              )}
              
              {/* Grid of events for this chunk - remove top margin on mobile */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 -mt-6 md:mt-0">
                {chunkEvents.map((event, eventIndex) => {
                  const actualIndex = chunkStart + eventIndex;
                  return renderEventCard(event, actualIndex);
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
